import { useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';

/**
 * Handles the scroll behaviour that the browser does NOT perform for a
 * client-side router:
 *
 * - When the URL carries a `#hash`, scroll the matching element into view.
 *   The element's own CSS `scroll-margin-top` keeps it clear of the fixed
 *   site header.
 * - On a normal route change (no hash), jump back to the top of the page so
 *   visitors do not land halfway down a freshly opened page.
 *
 * Without this, links such as `/how-it-works#setup` change the route but the
 * browser never scrolls to the anchor, so the target section is missed.
 */
export default function ScrollManager() {
  const { pathname, hash } = useLocation();
  const isFirstRender = useRef(true);

  useEffect(() => {
    const isFirst = isFirstRender.current;
    isFirstRender.current = false;

    if (hash) {
      const id = decodeURIComponent(hash.slice(1));
      const frame = requestAnimationFrame(() => {
        const target = document.getElementById(id);
        if (target) {
          target.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
      });
      return () => cancelAnimationFrame(frame);
    }

    /* Keep the very first paint as-is (e.g. a restored scroll position on reload). */
    if (!isFirst) {
      window.scrollTo(0, 0);
    }
    return undefined;
  }, [pathname, hash]);

  return null;
}